import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Separator } from "../components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Pencil, Trash, Plus, SignOut, Eye, UploadSimple, X, Images, Package, Camera, Gear, UsersThree, Tag, PaintBrush, Sticker, Notebook, Tote, House, Heart, Star, Flower, Palette, Hoodie, Coffee, Receipt, Check } from "@phosphor-icons/react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FALLBACK_CATEGORIES = ["Art Prints", "Stickers", "Stationery", "Accessories", "Home"];

export default function AdminPage() {
  const [adminKey, setAdminKey] = useState("");
  const [loggedIn, setLoggedIn] = useState(false);
  const [loginKey, setLoginKey] = useState("");
  const [activeTab, setActiveTab] = useState("products");

  // ─── Products state ───
  const [products, setProducts] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    name: "", description: "", price: "", category: "Art Prints",
    image: "", images: [], featured: false, in_stock: true,
  });

  // ─── Portfolio state ───
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [portfolioUploading, setPortfolioUploading] = useState(false);
  const [portfolioForm, setPortfolioForm] = useState({ title: "", link: "" });
  const portfolioFileRef = useRef(null);

  // ─── Settings state ───
  const [upiId, setUpiId] = useState("");
  const [upiName, setUpiName] = useState("");
  const [savingSettings, setSavingSettings] = useState(false);

  // ─── Orders state ───
  const [orders, setOrders] = useState([]);
  const [orderFilter, setOrderFilter] = useState("verification_pending");
  const [actingOrderId, setActingOrderId] = useState(null);
  const [previewOrder, setPreviewOrder] = useState(null);

  // ─── Subscribers state ───
  const [subscribers, setSubscribers] = useState([]);

  // ─── Categories state ───
  const [categoriesList, setCategoriesList] = useState([]);
  const [catForm, setCatForm] = useState({ name: "", icon: "tag" });
  const [editingCat, setEditingCat] = useState(null);
  const [showCatForm, setShowCatForm] = useState(false);

  // ─── Fetch data ───
  const fetchProducts = useCallback(async () => {
    try { const res = await axios.get(`${API}/products`); setProducts(res.data); } catch (err) { console.error(err); }
  }, []);

  const fetchPortfolio = useCallback(async () => {
    try { const res = await axios.get(`${API}/portfolio`); setPortfolioItems(res.data); } catch (err) { console.error(err); }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setUpiId(res.data.upi_id || "");
      setUpiName(res.data.upi_name || "");
    } catch (err) { console.error(err); }
  }, []);

  const fetchOrders = useCallback(async () => {
    if (!adminKey) return;
    try {
      const q = orderFilter && orderFilter !== "all" ? `?status=${orderFilter}` : "";
      const res = await axios.get(`${API}/admin/orders${q}`, { headers: { "x-admin-key": adminKey } });
      setOrders(res.data);
    } catch (err) { console.error(err); }
  }, [adminKey, orderFilter]);

  const fetchSubscribers = useCallback(async () => {
    try { const res = await axios.get(`${API}/admin/subscribers`, { headers: { "x-admin-key": adminKey } }); setSubscribers(res.data); } catch (err) { console.error(err); }
  }, [adminKey]);

  const fetchCategories = useCallback(async () => {
    try { const res = await axios.get(`${API}/categories/full`); setCategoriesList(res.data); } catch (err) { console.error(err); }
  }, []);

  useEffect(() => {
    if (loggedIn) { fetchProducts(); fetchPortfolio(); fetchSettings(); fetchSubscribers(); fetchCategories(); fetchOrders(); }
  }, [loggedIn, fetchProducts, fetchPortfolio, fetchSettings, fetchSubscribers, fetchCategories, fetchOrders]);

  const handleLogin = async () => {
    try { await axios.post(`${API}/admin/login`, { key: loginKey }); setAdminKey(loginKey); setLoggedIn(true); } catch { alert("Invalid admin key"); }
  };

  // ─── Product helpers ───
  const resetForm = () => {
    setForm({ name: "", description: "", price: "", category: "Art Prints", image: "", images: [], featured: false, in_stock: true });
    setEditingProduct(null); setShowForm(false);
  };

  const uploadFile = async (file) => {
    const allowed = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowed.includes(file.type)) { alert("Only JPEG, PNG, GIF, WebP allowed"); return null; }
    if (file.size > 10 * 1024 * 1024) { alert("Max 10MB"); return null; }
    const fd = new FormData(); fd.append("file", file);
    const res = await axios.post(`${API}/upload`, fd, { headers: { "x-admin-key": adminKey, "Content-Type": "multipart/form-data" } });
    return `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
  };

  const handleMultipleUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const urls = [];
      for (const file of files) { const url = await uploadFile(file); if (url) urls.push(url); }
      if (urls.length > 0) {
        setForm((prev) => { const ni = [...prev.images, ...urls]; return { ...prev, images: ni, image: prev.image || ni[0] }; });
        if (!editingProduct && !form.name && urls[0]) {
          setAnalyzing(true);
          try {
            const aiRes = await axios.post(`${API}/admin/analyze-image`, { image_url: urls[0] }, { headers: { "x-admin-key": adminKey } });
            if (aiRes.data.ai_success) {
              setForm((prev) => ({ ...prev, name: aiRes.data.name || prev.name, description: aiRes.data.description || prev.description, category: aiRes.data.category || prev.category, price: String(aiRes.data.price || prev.price) }));
            }
          } catch {} finally { setAnalyzing(false); }
        }
      }
    } catch { alert("Upload failed."); } finally { setUploading(false); }
  };

  const handleDrop = (e) => { e.preventDefault(); handleMultipleUpload(Array.from(e.dataTransfer.files)); };
  const handleFileSelect = (e) => { handleMultipleUpload(Array.from(e.target.files)); if (fileInputRef.current) fileInputRef.current.value = ""; };
  const removeImage = (idx) => { setForm((prev) => { const ni = prev.images.filter((_, i) => i !== idx); return { ...prev, images: ni, image: ni.length > 0 ? ni[0] : "" }; }); };
  const setPrimaryImage = (idx) => { setForm((prev) => ({ ...prev, image: prev.images[idx] })); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.image && form.images.length === 0) { alert("Please upload an image"); return; }
    const payload = { ...form, price: parseFloat(form.price), image: form.image || (form.images[0] || "") };
    try {
      if (editingProduct) { await axios.put(`${API}/admin/products/${editingProduct}`, payload, { headers: { "x-admin-key": adminKey } }); }
      else { await axios.post(`${API}/admin/products`, payload, { headers: { "x-admin-key": adminKey } }); }
      resetForm(); fetchProducts();
    } catch { alert("Error saving product"); }
  };

  const handleEdit = (p) => {
    setForm({ name: p.name, description: p.description, price: String(p.price), category: p.category, image: p.image, images: p.images || [p.image], featured: p.featured, in_stock: p.in_stock });
    setEditingProduct(p.id); setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete?")) return;
    try { await axios.delete(`${API}/admin/products/${id}`, { headers: { "x-admin-key": adminKey } }); fetchProducts(); } catch { alert("Error"); }
  };

  // ─── Portfolio helpers ───
  const handlePortfolioUpload = async (files) => {
    if (!files || files.length === 0) return;
    setPortfolioUploading(true);
    try {
      for (const file of files) {
        const url = await uploadFile(file);
        if (url) {
          await axios.post(`${API}/admin/portfolio`, { image: url, title: portfolioForm.title, link: portfolioForm.link }, { headers: { "x-admin-key": adminKey } });
        }
      }
      setPortfolioForm({ title: "", link: "" });
      fetchPortfolio();
    } catch { alert("Upload failed"); } finally { setPortfolioUploading(false); }
  };

  const handlePortfolioDrop = (e) => { e.preventDefault(); handlePortfolioUpload(Array.from(e.dataTransfer.files)); };
  const handlePortfolioFileSelect = (e) => { handlePortfolioUpload(Array.from(e.target.files)); if (portfolioFileRef.current) portfolioFileRef.current.value = ""; };
  const deletePortfolioItem = async (id) => {
    try { await axios.delete(`${API}/admin/portfolio/${id}`, { headers: { "x-admin-key": adminKey } }); fetchPortfolio(); } catch { alert("Error"); }
  };

  // ─── Settings helpers ───
  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(
        `${API}/admin/settings`,
        { upi_id: upiId, upi_name: upiName },
        { headers: { "x-admin-key": adminKey } }
      );
      alert("Settings saved!");
    } catch (err) {
      alert(err?.response?.data?.detail || "Error saving");
    } finally { setSavingSettings(false); }
  };

  // ─── Order admin helpers ───
  const approveOrder = async (orderId) => {
    setActingOrderId(orderId);
    try {
      await axios.put(`${API}/admin/orders/${orderId}/approve`, {}, { headers: { "x-admin-key": adminKey } });
      await fetchOrders();
    } catch (err) {
      alert(err?.response?.data?.detail || "Error approving order");
    } finally { setActingOrderId(null); }
  };

  const rejectOrder = async (orderId) => {
    const reason = prompt("Reason for rejection (optional):") || "";
    setActingOrderId(orderId);
    try {
      await axios.put(`${API}/admin/orders/${orderId}/reject`, { reason }, { headers: { "x-admin-key": adminKey } });
      await fetchOrders();
    } catch (err) {
      alert(err?.response?.data?.detail || "Error rejecting order");
    } finally { setActingOrderId(null); }
  };

  const deleteOrder = async (orderId) => {
    if (!confirm("Delete this order permanently?")) return;
    try {
      await axios.delete(`${API}/admin/orders/${orderId}`, { headers: { "x-admin-key": adminKey } });
      await fetchOrders();
    } catch { alert("Error deleting"); }
  };

  useEffect(() => {
    if (loggedIn) fetchOrders();
  }, [orderFilter, loggedIn, fetchOrders]);

  const deleteSubscriber = async (id) => {
    if (!window.confirm("Remove subscriber?")) return;
    try { await axios.delete(`${API}/admin/subscribers/${id}`, { headers: { "x-admin-key": adminKey } }); fetchSubscribers(); } catch { alert("Error"); }
  };

  // ─── Category helpers ───
  const ICON_OPTIONS = [
    { value: "paint-brush", label: "Paint Brush", Icon: PaintBrush },
    { value: "sticker", label: "Sticker", Icon: Sticker },
    { value: "notebook", label: "Notebook", Icon: Notebook },
    { value: "tote", label: "Tote Bag", Icon: Tote },
    { value: "house", label: "House", Icon: House },
    { value: "tag", label: "Tag", Icon: Tag },
    { value: "heart", label: "Heart", Icon: Heart },
    { value: "star", label: "Star", Icon: Star },
    { value: "flower", label: "Flower", Icon: Flower },
    { value: "palette", label: "Palette", Icon: Palette },
    { value: "hoodie", label: "Hoodie", Icon: Hoodie },
    { value: "coffee", label: "Coffee", Icon: Coffee },
  ];

  const getIconComponent = (iconName) => {
    const found = ICON_OPTIONS.find((o) => o.value === iconName);
    return found ? found.Icon : Tag;
  };

  const resetCatForm = () => { setCatForm({ name: "", icon: "tag" }); setEditingCat(null); setShowCatForm(false); };

  const handleCatSubmit = async (e) => {
    e.preventDefault();
    if (!catForm.name.trim()) return;
    try {
      if (editingCat) {
        await axios.put(`${API}/admin/categories/${editingCat}`, catForm, { headers: { "x-admin-key": adminKey } });
      } else {
        await axios.post(`${API}/admin/categories`, catForm, { headers: { "x-admin-key": adminKey } });
      }
      resetCatForm(); fetchCategories();
    } catch (err) { alert(err.response?.data?.detail || "Error saving category"); }
  };

  const handleEditCat = (cat) => { setCatForm({ name: cat.name, icon: cat.icon }); setEditingCat(cat.id); setShowCatForm(true); };

  const handleDeleteCat = async (id) => {
    if (!window.confirm("Delete category?")) return;
    try { await axios.delete(`${API}/admin/categories/${id}`, { headers: { "x-admin-key": adminKey } }); fetchCategories(); } catch { alert("Error"); }
  };

  // ─── Login ───
  if (!loggedIn) {
    return (
      <div data-testid="admin-login-page" className="min-h-screen flex items-center justify-center pt-20 px-6">
        <div className="w-full max-w-sm">
          <h1 className="text-3xl text-[#1A1A1A] mb-6 text-center" style={{ fontFamily: "'Lazy Dog', cursive" }}>admin</h1>
          <div className="space-y-4">
            <Input data-testid="admin-key-input" type="password" placeholder="enter admin key" value={loginKey} onChange={(e) => setLoginKey(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleLogin()} className="bg-[#EBD988] border-[#D4C470] rounded-lg h-11" />
            <Button data-testid="admin-login-btn" onClick={handleLogin} className="w-full bg-[#E01A23] hover:bg-[#c4161e] text-white h-11 rounded-full">log in</Button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Admin Dashboard ───
  return (
    <div data-testid="admin-dashboard" className="pt-20 md:pt-24 min-h-screen">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12 pb-20">
        {/* Header */}
        <div className="flex items-center justify-between py-8">
          <h1 className="text-3xl md:text-4xl tracking-tight text-[#1A1A1A]" style={{ fontFamily: "'Lazy Dog', cursive" }}>admin panel</h1>
          <Button data-testid="admin-logout-btn" variant="outline" onClick={() => { setLoggedIn(false); setAdminKey(""); }} className="border-[#D4C470] text-[#3a3a3a] hover:text-[#E01A23] rounded-full h-10">
            <SignOut size={16} />
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8" data-testid="admin-tabs">
          {[
            { id: "products", label: "products", icon: Package },
            { id: "orders", label: "orders", icon: Receipt },
            { id: "categories", label: "categories", icon: Tag },
            { id: "portfolio", label: "portfolio", icon: Camera },
            { id: "subscribers", label: "subscribers", icon: UsersThree },
            { id: "settings", label: "settings", icon: Gear },
          ].map((tab) => (
            <button
              key={tab.id}
              data-testid={`tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm transition-all ${
                activeTab === tab.id ? "bg-[#E01A23] text-white" : "bg-[#EBD988] text-[#3a3a3a] hover:text-[#1A1A1A]"
              }`}
              style={{ fontFamily: "'Courier New', Courier, monospace" }}
            >
              <tab.icon size={16} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* ═══ PRODUCTS TAB ═══ */}
        {activeTab === "products" && (
          <>
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-[#555]">{products.length} products</p>
              <Button data-testid="add-product-btn" onClick={() => { resetForm(); setShowForm(true); }} className="bg-[#E01A23] hover:bg-[#c4161e] text-white rounded-full h-10 px-5 text-sm">
                <Plus size={16} className="mr-1" /> add product
              </Button>
            </div>

            {showForm && (
              <div className="bg-[#EBD988] rounded-2xl p-6 md:p-8 mb-8">
                <div className="flex items-center gap-3 mb-6">
                  <h2 className="text-xl text-[#1A1A1A]" style={{ fontFamily: "'Lazy Dog', cursive" }}>{editingProduct ? "edit product" : "new product"}</h2>
                  {analyzing && <span data-testid="ai-analyzing" className="flex items-center gap-2 text-xs text-[#E01A23] bg-[#E01A23]/10 px-3 py-1 rounded-full"><span className="w-3 h-3 border-2 border-[#E01A23] border-t-transparent rounded-full animate-spin" />AI analyzing...</span>}
                  {!editingProduct && !analyzing && form.images.length > 0 && form.name && <span className="text-xs text-green-600 bg-green-100 px-3 py-1 rounded-full">AI filled - edit as needed</span>}
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div><label className="text-xs text-[#3a3a3a] mb-1 block">name</label><Input data-testid="product-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="bg-[#F5E6A0] border-[#D4C470] rounded-lg" /></div>
                    <div><label className="text-xs text-[#3a3a3a] mb-1 block">price ($)</label><Input data-testid="product-price-input" type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required className="bg-[#F5E6A0] border-[#D4C470] rounded-lg" /></div>
                  </div>
                  <div><label className="text-xs text-[#3a3a3a] mb-1 block">description</label><textarea data-testid="product-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required rows={3} className="w-full px-3 py-2 bg-[#F5E6A0] border border-[#D4C470] rounded-lg text-sm resize-none focus:outline-none focus:border-[#E01A23]" /></div>
                  <div><label className="text-xs text-[#3a3a3a] mb-1 block">category</label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger data-testid="product-category-select" className="bg-[#F5E6A0] border-[#D4C470] rounded-lg max-w-xs"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#F5E6A0] border-[#D4C470]">{(categoriesList.length > 0 ? categoriesList.map(c => c.name) : FALLBACK_CATEGORIES).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs text-[#3a3a3a] mb-2 flex items-center gap-1"><Images size={14} /> product images ({form.images.length})</label>
                    {form.images.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3" data-testid="image-gallery-admin">
                        {form.images.map((img, idx) => (
                          <div key={idx} className={`relative w-20 h-20 rounded-lg overflow-hidden group cursor-pointer border-2 transition-colors ${form.image === img ? "border-[#E01A23]" : "border-transparent hover:border-[#D4C470]"}`} onClick={() => setPrimaryImage(idx)} data-testid={`admin-image-${idx}`}>
                            <img src={img} alt="" className="w-full h-full object-cover" />
                            {form.image === img && <div className="absolute top-0.5 left-0.5 bg-[#E01A23] text-white text-[8px] px-1 rounded">primary</div>}
                            <button type="button" onClick={(e) => { e.stopPropagation(); removeImage(idx); }} data-testid={`remove-image-${idx}`} className="absolute top-0.5 right-0.5 w-5 h-5 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><X size={10} /></button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div onDrop={handleDrop} onDragOver={(e) => e.preventDefault()} onClick={() => fileInputRef.current?.click()} data-testid="image-dropzone" className="w-full h-[80px] bg-[#F5E6A0] border-2 border-dashed border-[#D4C470] hover:border-[#E01A23] rounded-lg flex items-center justify-center cursor-pointer transition-colors">
                      {uploading ? <div className="w-5 h-5 border-2 border-[#E01A23] border-t-transparent rounded-full animate-spin" /> : <div className="flex items-center gap-2 text-[#555]"><UploadSimple size={20} /><span className="text-xs">click or drag to upload (multiple)</span></div>}
                    </div>
                    <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" multiple onChange={handleFileSelect} className="hidden" data-testid="file-input" />
                  </div>
                  <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2 text-sm text-[#3a3a3a] cursor-pointer"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="accent-[#E01A23]" />featured</label>
                    <label className="flex items-center gap-2 text-sm text-[#3a3a3a] cursor-pointer"><input type="checkbox" checked={form.in_stock} onChange={(e) => setForm({ ...form, in_stock: e.target.checked })} className="accent-[#E01A23]" />in stock</label>
                  </div>
                  <div className="flex items-center gap-3 pt-2">
                    <Button data-testid="save-product-btn" type="submit" disabled={uploading} className="bg-[#E01A23] hover:bg-[#c4161e] text-white rounded-full h-10 px-6 text-sm">{editingProduct ? "update" : "create"}</Button>
                    <Button data-testid="cancel-form-btn" type="button" variant="outline" onClick={resetForm} className="border-[#D4C470] text-[#3a3a3a] rounded-full h-10 px-6 text-sm">cancel</Button>
                  </div>
                </form>
              </div>
            )}

            <Separator className="bg-[#E01A23]/10 mb-4" />
            <div className="space-y-3">
              {products.map((p) => (
                <div key={p.id} data-testid={`admin-product-${p.id}`} className="flex items-center gap-4 bg-[#EBD988] rounded-xl p-4 hover:bg-[#e8d87a] transition-colors">
                  <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 bg-[#D4C470]"><img src={p.image} alt="" className="w-full h-full object-cover" /></div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm text-[#1A1A1A] font-medium truncate">{p.name}</h3>
                    <p className="text-xs text-[#3a3a3a]">{p.category} &middot; ${p.price.toFixed(2)}{p.images && p.images.length > 1 && <span className="ml-2 text-[#E01A23]">&middot; {p.images.length} photos</span>}</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {p.featured && <span className="text-[10px] bg-[#E01A23]/10 text-[#E01A23] px-2 py-0.5 rounded-full">featured</span>}
                  </div>
                  <div className="flex items-center gap-1">
                    <a href={`/product/${p.id}`} target="_blank" rel="noopener noreferrer" className="p-2 text-[#555] hover:text-[#1A1A1A] transition-colors"><Eye size={16} /></a>
                    <button data-testid={`edit-product-${p.id}`} onClick={() => handleEdit(p)} className="p-2 text-[#555] hover:text-[#E01A23] transition-colors"><Pencil size={16} /></button>
                    <button data-testid={`delete-product-${p.id}`} onClick={() => handleDelete(p.id)} className="p-2 text-[#555] hover:text-[#E01A23] transition-colors"><Trash size={16} /></button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}


        {/* ═══ ORDERS TAB ═══ */}
        {activeTab === "orders" && (
          <>
            <div className="flex flex-wrap items-center gap-2 mb-6">
              {[
                { v: "verification_pending", l: "pending verification" },
                { v: "confirmed", l: "confirmed" },
                { v: "awaiting_payment", l: "awaiting payment" },
                { v: "rejected", l: "rejected" },
                { v: "all", l: "all" },
              ].map((f) => (
                <button
                  key={f.v}
                  data-testid={`orders-filter-${f.v}`}
                  onClick={() => setOrderFilter(f.v)}
                  className={`px-4 py-1.5 rounded-full text-xs transition-all ${
                    orderFilter === f.v ? "bg-[#E01A23] text-white" : "bg-[#EBD988] text-[#3a3a3a] hover:text-[#1A1A1A]"
                  }`}
                  style={{ fontFamily: "'Courier New', Courier, monospace" }}
                >
                  {f.l}
                </button>
              ))}
              <span className="text-xs text-[#555] ml-auto" style={{ fontFamily: "'Courier New', Courier, monospace" }}>{orders.length} orders</span>
            </div>

            <div className="space-y-3" data-testid="orders-list">
              {orders.length === 0 && (
                <p className="text-center text-[#555] py-8" style={{ fontFamily: "'Courier New', Courier, monospace" }}>no orders here yet</p>
              )}
              {orders.map((o) => {
                const statusColor =
                  o.status === "confirmed" ? "bg-green-100 text-green-700"
                  : o.status === "verification_pending" ? "bg-blue-100 text-blue-700"
                  : o.status === "rejected" ? "bg-red-100 text-red-700"
                  : "bg-amber-100 text-amber-700";
                const sym = "₹";
                return (
                  <div key={o.id} className="bg-[#EBD988] rounded-2xl p-4 md:p-5" data-testid={`order-row-${o.id}`}>
                    <div className="flex flex-wrap items-start gap-3 justify-between mb-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm text-[#1A1A1A] font-medium" style={{ fontFamily: "'Courier New', Courier, monospace" }}>
                            {o.order_number}
                          </p>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full ${statusColor}`} style={{ fontFamily: "'Courier New', Courier, monospace" }}>
                            {o.status.replace(/_/g, " ")}
                          </span>
                          {o.type === "subscription" && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-100 text-purple-700" style={{ fontFamily: "'Courier New', Courier, monospace" }}>
                              mail club
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-[#555] mt-1" style={{ fontFamily: "'Courier New', Courier, monospace" }}>
                          {o.customer?.name} · {o.customer?.email}
                          {o.customer?.phone ? ` · ${o.customer.phone}` : ""}
                        </p>
                        <p className="text-[10px] text-[#999] mt-0.5" style={{ fontFamily: "'Courier New', Courier, monospace" }}>
                          {new Date(o.created_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg text-[#E01A23]" style={{ fontFamily: "'Lazy Dog', cursive" }}>
                          {sym}{Number(o.amount).toFixed(2)}
                        </p>
                        <p className="text-[10px] text-[#555]" style={{ fontFamily: "'Courier New', Courier, monospace" }}>{o.items?.length || 0} item{(o.items?.length || 0) !== 1 ? "s" : ""}</p>
                      </div>
                    </div>

                    {/* Item summary */}
                    <div className="text-xs text-[#3a3a3a] mb-3" style={{ fontFamily: "'Courier New', Courier, monospace" }}>
                      {(o.items || []).map((it) => `${it.name} ×${it.quantity}`).join(" · ")}
                    </div>

                    {/* UTR + screenshot block */}
                    {o.utr_number && (
                      <div className="bg-[#F5E6A0] rounded-xl p-3 mb-3 flex flex-wrap items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-[10px] text-[#555] uppercase tracking-wider" style={{ fontFamily: "'Courier New', Courier, monospace" }}>UTR / Reference</p>
                          <p className="text-sm text-[#1A1A1A] break-all" data-testid={`order-utr-${o.id}`} style={{ fontFamily: "'Courier New', Courier, monospace" }}>{o.utr_number}</p>
                        </div>
                        {o.screenshot_url && (
                          <button
                            type="button"
                            data-testid={`view-screenshot-${o.id}`}
                            onClick={() => setPreviewOrder(o)}
                            className="flex items-center gap-1 px-3 py-2 rounded-lg border border-[#E01A23]/40 text-[#E01A23] hover:bg-[#E01A23] hover:text-white text-xs"
                            style={{ fontFamily: "'Courier New', Courier, monospace" }}
                          >
                            <Eye size={14} /> view screenshot
                          </button>
                        )}
                      </div>
                    )}

                    {/* Shipping */}
                    {o.shipping && (
                      <details className="text-xs text-[#3a3a3a] mb-3" style={{ fontFamily: "'Courier New', Courier, monospace" }}>
                        <summary className="cursor-pointer hover:text-[#E01A23]">shipping address</summary>
                        <p className="mt-1 leading-relaxed">
                          {o.shipping.address_line1}{o.shipping.address_line2 ? `, ${o.shipping.address_line2}` : ""}<br />
                          {o.shipping.city}, {o.shipping.state} {o.shipping.zip_code || o.shipping.zip}<br />
                          {o.shipping.country}
                        </p>
                      </details>
                    )}

                    <div className="flex items-center gap-2 flex-wrap">
                      {o.status === "verification_pending" && (
                        <>
                          <button
                            data-testid={`approve-order-${o.id}`}
                            onClick={() => approveOrder(o.id)}
                            disabled={actingOrderId === o.id}
                            className="flex items-center gap-1 px-4 py-2 rounded-full bg-green-600 hover:bg-green-700 text-white text-xs"
                            style={{ fontFamily: "'Courier New', Courier, monospace" }}
                          >
                            <Check size={14} weight="bold" /> approve
                          </button>
                          <button
                            data-testid={`reject-order-${o.id}`}
                            onClick={() => rejectOrder(o.id)}
                            disabled={actingOrderId === o.id}
                            className="flex items-center gap-1 px-4 py-2 rounded-full bg-[#E01A23] hover:bg-[#c4161e] text-white text-xs"
                            style={{ fontFamily: "'Courier New', Courier, monospace" }}
                          >
                            <X size={14} weight="bold" /> reject
                          </button>
                        </>
                      )}
                      <button
                        data-testid={`delete-order-${o.id}`}
                        onClick={() => deleteOrder(o.id)}
                        className="ml-auto p-2 text-[#555] hover:text-[#E01A23] transition-colors"
                        title="Delete order"
                      >
                        <Trash size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Screenshot modal */}
            {previewOrder && (
              <div
                className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
                onClick={() => setPreviewOrder(null)}
                data-testid="screenshot-modal"
              >
                <div className="bg-[#F5E6A0] rounded-2xl p-4 max-w-2xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm text-[#1A1A1A]" style={{ fontFamily: "'Courier New', Courier, monospace" }}>
                      payment screenshot · {previewOrder.order_number}
                    </p>
                    <button onClick={() => setPreviewOrder(null)} className="p-2 text-[#555] hover:text-[#E01A23]"><X size={18} /></button>
                  </div>
                  <img
                    src={previewOrder.screenshot_url.startsWith("http") ? previewOrder.screenshot_url : `${process.env.REACT_APP_BACKEND_URL}${previewOrder.screenshot_url}`}
                    alt="Payment screenshot"
                    className="w-full h-auto rounded-xl"
                    data-testid="screenshot-preview-image"
                  />
                </div>
              </div>
            )}
          </>
        )}

        {/* ═══ CATEGORIES TAB ═══ */}
        {activeTab === "categories" && (
          <>
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-[#555]">{categoriesList.length} categories</p>
              <Button data-testid="add-category-btn" onClick={() => { resetCatForm(); setShowCatForm(true); }} className="bg-[#E01A23] hover:bg-[#c4161e] text-white rounded-full h-10 px-5 text-sm">
                <Plus size={16} className="mr-1" /> add category
              </Button>
            </div>

            {showCatForm && (
              <div className="bg-[#EBD988] rounded-2xl p-6 mb-6">
                <h2 className="text-xl text-[#1A1A1A] mb-4" style={{ fontFamily: "'Lazy Dog', cursive" }}>{editingCat ? "edit category" : "new category"}</h2>
                <form onSubmit={handleCatSubmit} className="space-y-4">
                  <div>
                    <label className="text-xs text-[#555] mb-1 block">category name</label>
                    <Input data-testid="category-name-input" value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} required placeholder="e.g. Ceramics, Prints, Jewelry..." className="bg-[#F5E6A0] border-[#D4C470] rounded-lg max-w-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-[#555] mb-2 block">icon (vector)</label>
                    <div className="flex flex-wrap gap-2" data-testid="icon-picker">
                      {ICON_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setCatForm({ ...catForm, icon: opt.value })}
                          data-testid={`icon-option-${opt.value}`}
                          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border-2 transition-all text-xs ${
                            catForm.icon === opt.value
                              ? "border-[#E01A23] bg-[#E01A23]/10 text-[#E01A23]"
                              : "border-[#D4C470] bg-[#F5E6A0] text-[#3a3a3a] hover:border-[#E01A23]/50"
                          }`}
                        >
                          <opt.Icon size={18} weight="duotone" />
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Button data-testid="save-category-btn" type="submit" className="bg-[#E01A23] hover:bg-[#c4161e] text-white rounded-full h-10 px-6 text-sm">{editingCat ? "update" : "create"}</Button>
                    <Button data-testid="cancel-category-btn" type="button" variant="outline" onClick={resetCatForm} className="border-[#D4C470] text-[#555] rounded-full h-10 px-6 text-sm">cancel</Button>
                  </div>
                </form>
              </div>
            )}

            <div className="space-y-3">
              {categoriesList.map((cat) => {
                const IconComp = getIconComponent(cat.icon);
                return (
                  <div key={cat.id} data-testid={`admin-cat-${cat.id}`} className="flex items-center gap-4 bg-[#EBD988] rounded-xl p-4 hover:bg-[#e8d87a] transition-colors">
                    <div className="w-10 h-10 rounded-lg bg-[#E01A23]/10 flex items-center justify-center">
                      <IconComp size={22} weight="duotone" className="text-[#E01A23]" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm text-[#1A1A1A] font-medium">{cat.name}</h3>
                      <p className="text-xs text-[#555]">icon: {cat.icon}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button data-testid={`edit-cat-${cat.id}`} onClick={() => handleEditCat(cat)} className="p-2 text-[#555] hover:text-[#E01A23] transition-colors"><Pencil size={16} /></button>
                      <button data-testid={`delete-cat-${cat.id}`} onClick={() => handleDeleteCat(cat.id)} className="p-2 text-[#555] hover:text-[#E01A23] transition-colors"><Trash size={16} /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}


        {/* ═══ PORTFOLIO TAB ═══ */}
        {activeTab === "portfolio" && (
          <>
            <div className="bg-[#EBD988] rounded-2xl p-6 md:p-8 mb-8">
              <h2 className="text-xl text-[#1A1A1A] mb-4" style={{ fontFamily: "'Lazy Dog', cursive" }}>add to portfolio</h2>
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <Input placeholder="title (optional)" value={portfolioForm.title} onChange={(e) => setPortfolioForm({ ...portfolioForm, title: e.target.value })} className="bg-[#F5E6A0] border-[#D4C470] rounded-lg" />
                <Input placeholder="link URL (optional)" value={portfolioForm.link} onChange={(e) => setPortfolioForm({ ...portfolioForm, link: e.target.value })} className="bg-[#F5E6A0] border-[#D4C470] rounded-lg" />
              </div>
              <div
                onDrop={handlePortfolioDrop}
                onDragOver={(e) => e.preventDefault()}
                onClick={() => portfolioFileRef.current?.click()}
                data-testid="portfolio-dropzone"
                className="w-full h-[100px] bg-[#F5E6A0] border-2 border-dashed border-[#D4C470] hover:border-[#E01A23] rounded-lg flex items-center justify-center cursor-pointer transition-colors"
              >
                {portfolioUploading ? (
                  <div className="w-5 h-5 border-2 border-[#E01A23] border-t-transparent rounded-full animate-spin" />
                ) : (
                  <div className="flex items-center gap-2 text-[#555]">
                    <UploadSimple size={24} />
                    <span className="text-sm">click or drag photos for the portfolio</span>
                  </div>
                )}
              </div>
              <input ref={portfolioFileRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" multiple onChange={handlePortfolioFileSelect} className="hidden" data-testid="portfolio-file-input" />
            </div>

            <p className="text-sm text-[#555] mb-4">{portfolioItems.length} items in portfolio</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {portfolioItems.map((item) => (
                <div key={item.id} data-testid={`portfolio-admin-${item.id}`} className="relative group rounded-xl overflow-hidden bg-[#EBD988]">
                  <img src={item.image} alt={item.title || ""} className="w-full aspect-square object-cover" />
                  {item.title && <p className="text-xs text-[#3a3a3a] p-2 truncate">{item.title}</p>}
                  <button
                    data-testid={`delete-portfolio-${item.id}`}
                    onClick={() => deletePortfolioItem(item.id)}
                    className="absolute top-2 right-2 w-7 h-7 bg-black/50 hover:bg-[#E01A23] text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all"
                  >
                    <Trash size={14} />
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ═══ SUBSCRIBERS TAB ═══ */}
        {activeTab === "subscribers" && (
          <>
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-[#555]">{subscribers.length} subscribers</p>
              <div className="flex gap-3 text-xs text-[#3a3a3a]">
                <span className="bg-green-100 text-green-600 px-2 py-1 rounded-full">{subscribers.filter(s => s.type === "digital").length} digital</span>
                <span className="bg-[#E01A23]/10 text-[#E01A23] px-2 py-1 rounded-full">{subscribers.filter(s => s.type === "postal").length} postal</span>
              </div>
            </div>
            <div className="space-y-3">
              {subscribers.map((sub) => (
                <div key={sub.id} data-testid={`subscriber-${sub.id}`} className="flex items-center gap-4 bg-[#EBD988] rounded-xl p-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#1A1A1A] font-medium">{sub.email}</p>
                    <p className="text-xs text-[#3a3a3a]">
                      {sub.name || "—"} &middot; {sub.type}
                      {sub.plan && ` (${sub.plan})`}
                      {sub.status && <span className={`ml-2 ${sub.status === "active" ? "text-green-600" : "text-orange-500"}`}>&middot; {sub.status}</span>}
                    </p>
                    {sub.address && (
                      <p className="text-xs text-[#555] mt-1">{sub.address.line1}, {sub.address.city}, {sub.address.state} {sub.address.zip}</p>
                    )}
                  </div>
                  <button data-testid={`delete-sub-${sub.id}`} onClick={() => deleteSubscriber(sub.id)} className="p-2 text-[#555] hover:text-[#E01A23] transition-colors"><Trash size={16} /></button>
                </div>
              ))}
              {subscribers.length === 0 && <p className="text-center text-[#555] py-8">no subscribers yet</p>}
            </div>
          </>
        )}

        {/* ═══ SETTINGS TAB ═══ */}
        {activeTab === "settings" && (
          <div className="bg-[#EBD988] rounded-2xl p-6 md:p-8">
            <h2 className="text-xl text-[#1A1A1A] mb-6" style={{ fontFamily: "'Lazy Dog', cursive" }}>site settings</h2>
            <div className="space-y-4">
              <div className="bg-[#F5E6A0] border border-[#D4C470] rounded-xl p-4">
                <p className="text-xs text-[#555] uppercase tracking-wider mb-1" style={{ fontFamily: "'Courier New', Courier, monospace" }}>store currency</p>
                <p className="text-2xl text-[#E01A23]" style={{ fontFamily: "'Lazy Dog', cursive" }} data-testid="fixed-currency-label">INR (₹) — fixed</p>
                <p className="text-xs text-[#555] mt-2" style={{ fontFamily: "'Courier New', Courier, monospace" }}>all prices are entered and paid in indian rupees.</p>
              </div>

              <div className="border-t border-[#D4C470] pt-5">
                <p className="text-sm text-[#1A1A1A] mb-3" style={{ fontFamily: "'Lazy Dog', cursive" }}>UPI payment settings</p>
                <div className="grid sm:grid-cols-2 gap-3 mb-2">
                  <div>
                    <label className="text-xs text-[#3a3a3a] mb-1 block">UPI ID *</label>
                    <Input
                      data-testid="upi-id-input"
                      value={upiId}
                      onChange={(e) => setUpiId(e.target.value)}
                      placeholder="yourname@bank"
                      className="bg-[#F5E6A0] border-[#D4C470] rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-[#3a3a3a] mb-1 block">Display name (shown in UPI app)</label>
                    <Input
                      data-testid="upi-name-input"
                      value={upiName}
                      onChange={(e) => setUpiName(e.target.value)}
                      placeholder="Vandy's"
                      className="bg-[#F5E6A0] border-[#D4C470] rounded-lg"
                    />
                  </div>
                </div>
                <p className="text-xs text-[#555] mt-1">all customer checkouts will be paid to this UPI ID. only UPI payments are accepted (stripe disabled).</p>
              </div>

              <Button
                data-testid="save-settings-btn"
                onClick={saveSettings}
                disabled={savingSettings}
                className="bg-[#E01A23] hover:bg-[#c4161e] text-white rounded-full h-10 px-6 text-sm"
              >
                {savingSettings ? "saving..." : "save settings"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
